import cloneDeep from 'lodash/cloneDeep'

type QueryOptions = {
  query: {
    [key: string]: string | string[] | undefined
  }
}

export type Facet = {
  name: string
  keyword: string
  slug: string
  count: number
  slugs: {
    slug: string
    label: string
  }[]
}

export type DirectoryLinkInfo = {
  directoryPath: string
  keyword?: string
  page?: number
  directoryProviders?: Provider[]
  location?: Location | null
  facetSlugs?: string[]
  directoryResultsType?: string
}

export type DirectoryResultsType = {
  keyword?: string
  type?: string
  href?: string
}

export type Provider = {
  facetSlug: string
  facetKeyword: string | null
  profilesBasePath?: string
  jobsBasePath?: string
}

export const Page = (router: QueryOptions): number => {
  const pageValue = router.query.page
  if (pageValue == null || typeof pageValue === 'undefined') {
    return 1
  }
  return Number(pageValue)
}

export const Keyword = (router: QueryOptions): string => {
  return router.query.q as string
}

export type Location = {
  type?: string
  useNear?: boolean
  country?: string
  state?: string
  city?: string
  neighborhood?: string
}

export const getQueryLocation = (router: QueryOptions): Location | null => {
  const locations = router.query.locations as string

  if (!locations) {
    return null
  }

  const lowerCaseLocations = locations.toLowerCase()

  if (lowerCaseLocations === 'everywhere') {
    return null
  }

  if (lowerCaseLocations === 'near') {
    return {
      useNear: true,
    }
  }

  if (lowerCaseLocations === 'canada' || lowerCaseLocations === 'usa') {
    return {
      country: lowerCaseLocations,
    }
  }

  const lowerCaseLocationValues = lowerCaseLocations && lowerCaseLocations.split(',')
  if (lowerCaseLocationValues.length === 1) {
    return {
      state: lowerCaseLocationValues[0],
    }
  }

  if (lowerCaseLocationValues.length === 2) {
    return {
      state: lowerCaseLocationValues[1],
      city: lowerCaseLocationValues[0],
    }
  }

  return {
    state: lowerCaseLocationValues[2],
    city: lowerCaseLocationValues[1],
    neighborhood: lowerCaseLocationValues[0],
  }
}

export const Facets = (router: QueryOptions) => {
  const facets = router.query.facets as string
  if (!facets) {
    return null
  }
  return facets.toLowerCase().split(',').sort()
}

export const DirectoryLinkHref = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
  page,
  keyword,
}: DirectoryLinkInfo) => {
  const directoryLinkPath = DirectoryLinkPath({
    directoryProviders,
    directoryPath,
    location,
    facetSlugs,
  })
  let query = {}

  const facetsQueryValue = getFacetsPath(facetSlugs, directoryProviders)
  const locationsQueryValue = getLocationPath(location)
  if (keyword) {
    query = {
      ...query,
      q: keyword,
    }
  }
  if (facetsQueryValue) {
    query = {
      ...query,
      facets: facetsQueryValue,
    }

    if (locationsQueryValue) {
      query = {
        ...query,
        locations: locationsQueryValue,
      }
    }
  } else if (hasLocations(location) && locationsQueryValue) {
    query = {
      ...query,
      locations: locationsQueryValue,
    }
  }

  if (page != null && page > 1) {
    query = {
      ...query,
      page: page,
    }
  }
  if (query !== {}) {
    return { pathname: directoryLinkPath, query: query }
  }

  return { pathname: directoryPath }
}

const getLocationPath = (location?: Location) => {
  if (!location || location.country === null) {
    return 'everywhere'
  }

  if (location.useNear) {
    return 'near'
  }

  if (location.city && location.state && location.neighborhood) {
    return `${location.neighborhood},${location.city},${location.state}`
  }

  if (location.city && location.state) {
    return `${location.city},${location.state}`
  }

  if (location.state) {
    return `${location.state}`
  }

  return `${location.country}`
}

function hasFacets(facetSlugs: string[], directoryProviders: Provider[]) {
  const facets = getFacetsForUrl(facetSlugs, directoryProviders)
  return facets && facets.length > 0
}

function hasLocations(location: Location) {
  return location && !(location.type === LOCATION_TYPES.COUNTRIES && location.country === null)
}

export const DirectoryLinkPath = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
}: DirectoryLinkInfo) => {
  if (hasFacets(facetSlugs, directoryProviders)) {
    return `${directoryPath}/[locations]/[facets]`
  }

  if (hasLocations(location)) {
    return `${directoryPath}/[locations]`
  }

  return `${directoryPath}`
}

const getProviderFacetSlugs = (providers: Provider[]): string[] => {
  const results = []
  if (!providers || providers.length === 0) {
    return results
  }

  if (providersContain(providers, PROVIDERS.BABYSITTERS)) {
    if (providers.length <= 2) {
      return results
    }
    return providersRemove(providers, PROVIDERS.BABYSITTERS).map(
      (provider) => provider.facetKeyword
    )
  }

  if (providers.length === 1) {
    return results
  }
  return providers.map((provider) => provider.facetKeyword)
}

function getFacetsForUrl(facetSlugs?: string[], directoryProviders?: Provider[]): string[] {
  const hasFacetSlugs = facetSlugs?.length > 0
  const hasDirectoryProviders = directoryProviders?.length > 0
  if (hasFacetSlugs) {
    if (hasDirectoryProviders) {
      const providerFacetSlugs = getProviderFacetSlugs(directoryProviders)
      if (providerFacetSlugs && providerFacetSlugs.length > 0) {
        return [...facetSlugs, ...providerFacetSlugs]
      }
    }
    return facetSlugs
  }

  if (hasDirectoryProviders) {
    return getProviderFacetSlugs(directoryProviders)
  }

  return []
}

function getFacetsPath(facetSlugs: string[], directoryProviders?: Provider[]): string {
  const facets = getFacetsForUrl(facetSlugs, directoryProviders)
  if (!facets || facets.length === 0) {
    return null
  }

  return facets.sort().join(',')
}

const directoryLinkAsPathname = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
}: DirectoryLinkInfo) => {
  if (hasFacets(facetSlugs, directoryProviders)) {
    const locationPath = getLocationPath(location)
    const facetsPath = getFacetsPath(facetSlugs, directoryProviders)
    if (facetsPath) {
      return `${directoryPath}/${locationPath}/${facetsPath}`
    }
  }

  if (location && !(location.type === LOCATION_TYPES.COUNTRIES && location.country === null)) {
    const locationPath = getLocationPath(location)
    return `${directoryPath}/${locationPath}`
  }

  return `${directoryPath}`
}

export const getAsForDirectoryLink = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
  page,
  keyword,
}: DirectoryLinkInfo) => {
  const pathname = directoryLinkAsPathname({
    directoryProviders,
    directoryPath,
    location,
    facetSlugs,
  })
  const hasPage = page && page > 1
  if (hasPage && keyword) {
    return `${pathname}?page=${page}&q=${keyword}`
  } else if (hasPage) {
    return `${pathname}?page=${page}`
  } else if (keyword) {
    return `${pathname}?q=${keyword}`
  }

  return pathname
}

const getDefaultSiteDirectoryResultsType = (
  siteDirectoryResultsTypes: DirectoryResultsType[],
  directoryResultsType: string
) => {
  for (const siteDirectoryResultsType of siteDirectoryResultsTypes) {
    if (siteDirectoryResultsType.type === directoryResultsType) {
      return siteDirectoryResultsType
    }
  }
  return null
}

export const WithResultType = (
  directoryLinkInformation: DirectoryLinkInfo,
  siteDirectoryResultsTypes: DirectoryResultsType[],
  directoryResultsType: string
) => {
  if (!directoryLinkInformation) {
    return null
  }

  const newDirectoryLinkInformation: DirectoryLinkInfo = cloneDeep(directoryLinkInformation)
  newDirectoryLinkInformation.page = 1
  newDirectoryLinkInformation.directoryPath = getDirectoryPath(
    newDirectoryLinkInformation.directoryProviders,
    directoryResultsType,
    siteDirectoryResultsTypes
  )
  newDirectoryLinkInformation.directoryResultsType = directoryResultsType

  return newDirectoryLinkInformation
}

export const Reset = (
  directoryLinkInformation: DirectoryLinkInfo,
  siteDirectoryResultsTypes: DirectoryResultsType[]
) => {
  if (!directoryLinkInformation) {
    return null
  }

  const defaultSiteDirectoryResultsType = getDefaultSiteDirectoryResultsType(
    siteDirectoryResultsTypes,
    directoryLinkInformation.directoryResultsType
  )
  if (
    defaultSiteDirectoryResultsType.href === '/sitters' ||
    defaultSiteDirectoryResultsType.href === '/sitter-jobs'
  ) {
    if (directoryLinkInformation.directoryResultsType === RESULTS_TYPES.PROFILES) {
      return {
        page: 1,
        directoryPath: PROVIDERS.BABYSITTERS.profilesBasePath,
        directoryResultsType: directoryLinkInformation.directoryResultsType,
      }
    }
    return {
      page: 1,
      directoryPath: PROVIDERS.BABYSITTERS.jobsBasePath,
      directoryResultsType: directoryLinkInformation.directoryResultsType,
    }
  }
  return {
    page: 1,
    directoryPath: defaultSiteDirectoryResultsType.href,
    directoryResultsType: directoryLinkInformation.directoryResultsType,
  }
}

export const Provider = (slug: string, keyword: string = null) => {
  if (!slug) {
    return null
  }

  for (const provider of Object.values(PROVIDERS)) {
    if (
      provider.facetSlug === slug ||
      provider.facetKeyword === slug ||
      (provider.facetKeyword && provider.facetKeyword === keyword)
    ) {
      return provider
    }
  }

  return null
}

export const Providers = (facetSlugs?: string[]): Provider[] => {
  const providers = []
  if (!facetSlugs) {
    return providers
  }

  for (const facetSlug of facetSlugs) {
    const provider = Provider(facetSlug)
    if (provider) {
      providers.push(provider)
    }
  }

  return providers
}

export const NonProviderSlugs = (facetSlugs: string[]) => {
  const nonProviderSlugs: string[] = []
  if (!facetSlugs) {
    return nonProviderSlugs
  }

  for (const facetSlug of facetSlugs) {
    if (!isProvider(facetSlug)) {
      nonProviderSlugs.push(facetSlug)
    }
  }

  return nonProviderSlugs
}

const providersContain = (providers?: Provider[], provider?: Provider) => {
  if (!provider || !providers || providers.length === 0) {
    return false
  }
  for (const givenProvider of providers) {
    if (providersMatch(givenProvider, provider)) {
      return true
    }
  }
  return false
}

function providersMatch(givenProvider?: Provider, provider?: Provider) {
  if (!givenProvider || !provider) {
    return false
  }
  return (
    givenProvider.facetSlug === provider.facetSlug ||
    (givenProvider.facetKeyword != null && givenProvider.facetKeyword === provider.facetKeyword)
  )
}

const providersRemove = (providers?: Provider[], provider?: Provider): Provider[] => {
  if (!providers || providers.length === 0) {
    return []
  }

  if (!provider) {
    return providers
  }

  const results = []
  for (const givenProvider of providers) {
    if (!providersMatch(givenProvider, provider)) {
      results.push(givenProvider)
    }
  }
  return results
}

const isProvider = (slug: string, keyword?: string) => {
  const provider = Provider(slug, keyword)

  return provider != null
}

export const removeDuplicateProviders = (providers?: Provider[]) => {
  const results = []
  if (!providers) {
    return results
  }

  for (const provider of providers) {
    if (!providersContain(results, provider)) {
      results.push(provider)
    }
  }

  return results
}

const removeDuplicateFacetSlugs = (facetSlugs?: string[]) => {
  const results = []

  if (!facetSlugs) {
    return results
  }

  for (const facetSlug of facetSlugs) {
    if (!results.includes(facetSlug)) {
      results.push(facetSlug)
    }
  }

  return results
}

function getAllProviders(
  directoryProviders?: Provider[],
  provider: Provider | null = null
): Provider[] {
  const providers = directoryProviders || []
  if (provider) {
    providers.push(provider)
  }

  const nonDuplicatedProviders = removeDuplicateProviders(providers)
  if (
    providersMatch(PROVIDERS.INDIVIDUALS, provider) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  ) {
    return providersRemove(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  } else if (
    providersMatch(PROVIDERS.BUSINESS, provider) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  ) {
    return providersRemove(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS)
  }
  return nonDuplicatedProviders
}

function toggleProvider(directoryProviders: Provider[], provider: Provider) {
  if (providersContain(directoryProviders, provider)) {
    return getAllProviders(providersRemove(directoryProviders, provider))
  }
  return getAllProviders(directoryProviders, provider)
}

function getDirectoryPath(
  providers: Provider[],
  directoryResultsType: string,
  siteDirectoryResultsTypes: DirectoryResultsType[]
) {
  if (providers.length === 1) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return providers[0].profilesBasePath
    }
    return providers[0].jobsBasePath
  } else if (providers.length > 2 && providersContain(providers, PROVIDERS.BABYSITTERS)) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return PROVIDERS.BABYSITTERS.profilesBasePath
    }
    return PROVIDERS.BABYSITTERS.jobsBasePath
  } else if (providers.length === 2 && providersContain(providers, PROVIDERS.BABYSITTERS)) {
    const nonBabysitterProviders = providersRemove(providers, PROVIDERS.BABYSITTERS)
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return nonBabysitterProviders[0].profilesBasePath
    }
    return nonBabysitterProviders[0].jobsBasePath
  }
  const siteDirectoryResultsType = getDefaultSiteDirectoryResultsType(
    siteDirectoryResultsTypes,
    directoryResultsType
  )

  if (
    siteDirectoryResultsType.href === '/sitters' ||
    siteDirectoryResultsType.href === '/sitter-jobs'
  ) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return PROVIDERS.BABYSITTERS.profilesBasePath
    }
    return PROVIDERS.BABYSITTERS.jobsBasePath
  }
  return siteDirectoryResultsType.href
}

export const WithProvider = (
  directoryLinkInformation: DirectoryLinkInfo,
  siteDirectoryResultsTypes: DirectoryResultsType[],
  provider: Provider
): DirectoryLinkInfo | null => {
  if (!directoryLinkInformation || !siteDirectoryResultsTypes || !provider) {
    return null
  }
  const newDirectoryLinkInformation: DirectoryLinkInfo = cloneDeep(directoryLinkInformation)

  const providers = toggleProvider(newDirectoryLinkInformation.directoryProviders, provider)
  return {
    location: newDirectoryLinkInformation.location,
    page: 1,
    directoryProviders: providers,
    directoryPath: getDirectoryPath(
      providers,
      newDirectoryLinkInformation.directoryResultsType,
      siteDirectoryResultsTypes
    ),
    directoryResultsType: newDirectoryLinkInformation.directoryResultsType,
    facetSlugs: newDirectoryLinkInformation.facetSlugs,
  }
}

const toggleFacetSlugs = (facetSlugs: string[], facetSlug: string): string[] => {
  const nonDuplicateFacetSlugs = removeDuplicateFacetSlugs(facetSlugs)
  if (nonDuplicateFacetSlugs) {
    if (nonDuplicateFacetSlugs.includes(facetSlug)) {
      const filter = nonDuplicateFacetSlugs.filter((item) => item !== facetSlug)
      return filter
    }
    nonDuplicateFacetSlugs.push(facetSlug)
    return nonDuplicateFacetSlugs
  }

  return [facetSlug]
}

export const withLocation = (data?: DirectoryLinkInfo, location?: Location) => {
  if (!data || !location) {
    return null
  }

  const newData: DirectoryLinkInfo = cloneDeep(data)

  return {
    ...newData,
    page: 1,
    location: location,
  }
}

export const toggleFacetSlug = (
  data: DirectoryLinkInfo,
  facetSlug: string
): DirectoryLinkInfo | null => {
  if (!data || !facetSlug) {
    return null
  }

  const newData: DirectoryLinkInfo = cloneDeep(data)

  return {
    ...newData,
    page: 1,
    facetSlugs: toggleFacetSlugs(newData.facetSlugs, facetSlug),
  }
}

export const withPage = (data: DirectoryLinkInfo, page: number): DirectoryLinkInfo | null => {
  if (!data) {
    return null
  }

  const newData: DirectoryLinkInfo = cloneDeep(data)

  newData.page = page

  return newData
}

export const withKeyword = (data: DirectoryLinkInfo, keyword: string): DirectoryLinkInfo | null => {
  if (!data) {
    return null
  }

  const newData: DirectoryLinkInfo = cloneDeep(data)

  newData.keyword = keyword

  return newData
}

export const RESULTS_TYPES = {
  JOBS: 'jobs',
  PROFILES: 'profiles',
}

export const PROVIDERS: { [key: string]: Provider } = {
  INDIVIDUALS: {
    facetSlug: 'individuals',
    facetKeyword: 'individuals',
    profilesBasePath: '/individuals',
    jobsBasePath: '/individuals-jobs',
  },
  DAYCARES: {
    facetSlug: 'services_daycare',
    facetKeyword: 'daycare',
    profilesBasePath: '/daycares',
    jobsBasePath: '/daycare-jobs',
  },
  NANNIES: {
    facetSlug: 'services_nanny',
    facetKeyword: 'nanny',
    profilesBasePath: '/nannies',
    jobsBasePath: '/nanny-jobs',
  },
  BUSINESS: {
    facetSlug: 'business',
    facetKeyword: 'business',
    profilesBasePath: '/businesses',
    jobsBasePath: '/business-jobs',
  },
  BABYSITTERS: {
    facetSlug: 'services_babysitter',
    facetKeyword: 'babysitter',
    profilesBasePath: '/babysitters',
    jobsBasePath: '/babysitter-jobs',
  },
}

export const LOCATION_TYPES = {
  COUNTRIES: 'countries',
  STATES: 'states',
  CITIES: 'cities',
  NEIGHBORHOODS: 'neighborhoods',
}
